import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ProfileMenu.styles';
import { ProfileMenuLoginBtn } from './components/ProfileMenuLoginBtn';
import { ProfileMenuBtn } from './components/ProfileMenuBtn';
import { ProfileMenuIcon } from './components/ProfileMenuIcon';
import { getProfileMenuItems } from './ProfileMenu.helpers';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { useDesktopCatalogueStore } from '@/store/useDesktopCatalogueStore';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/useAuthStore';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { Dropdown } from '@/components/UI/Dropdown';
import { ROUTES } from '@/constants/routes';
import { formatPhoneNumber } from '@/utils/helpers';
import { CustomLink } from '@/components/CustomLink/CustomLink';

export const ProfileMenu = () => {
  const { t } = useTranslation();
  const [isDropdownOpen, toggleDropdown] = useToggleOpenState();
  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);
  const [isDesktopCatalogueOpen, toggleDesktopCatalogue] =
    useDesktopCatalogueStore(state => [
      state.isDesktopCatalogueOpen,
      state.toggleDesktopCatalogue,
    ]);
  const { logoutUser } = useAuth();
  const [isAuthenticated, userInfo] = useAuthStore(state => [
    state.isAuthenticated,
    state.userInfo,
  ]);
  const { authenticationPhone, legacyId } = userInfo ?? {};

  function handleBtnClick() {
    toggleDropdown();

    if (isNavOpen) {
      toggleNav();
    }

    if (isDesktopCatalogueOpen) {
      toggleDesktopCatalogue();
    }
  }

  function handleLogout(event: React.MouseEvent) {
    event.preventDefault();
    logoutUser();
  }

  if (!isAuthenticated) {
    return <ProfileMenuLoginBtn />;
  }

  return (
    <ProfileMenuBtn
      dataTestId={CommonTestIds.profileMenu.button}
      isDropdownOpen={isDropdownOpen}
      onClick={handleBtnClick}
    >
      <Dropdown isOpen={isDropdownOpen} onClose={() => toggleDropdown(false)}>
        <S.ProfileMenuContent data-testid={CommonTestIds.profileMenu.content}>
          <S.ProfileInfoContainer href={ROUTES.profile}>
            <ProfileMenuIcon />
            <S.ProfileInfo>
              <S.ProfilePhoneNumber>
                {authenticationPhone && formatPhoneNumber(authenticationPhone)}
              </S.ProfilePhoneNumber>
              <S.ProfileNumber>
                {t('pages.cabinet.profile_number')}: {legacyId}
              </S.ProfileNumber>
            </S.ProfileInfo>
          </S.ProfileInfoContainer>
          <S.ProfileMenuItemsList>
            {getProfileMenuItems(handleLogout).map(
              ({ href, translationKey, onClick, dataStat }) => (
                <S.ProfileMenuItem key={href}>
                  <CustomLink
                    data-stat={dataStat}
                    href={href}
                    onClick={onClick}
                  >
                    <S.ProfileMenuItemText>
                      {t(translationKey)}
                    </S.ProfileMenuItemText>
                  </CustomLink>
                </S.ProfileMenuItem>
              )
            )}
          </S.ProfileMenuItemsList>
        </S.ProfileMenuContent>
      </Dropdown>
    </ProfileMenuBtn>
  );
};
